import React, { useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { fetchCallRecordsForExtension } from 'flow/actions/extension-actions';
import CentralSwitchEmptyState from 'flow/components/central-switch/CentralSwitchEmptyState';
import CentralSwitchHeader from 'flow/components/central-switch/CentralSwitchHeader';
import ListItemContact from 'flow/components/ui-components/ListItemContact';
import { usePrevious } from 'flow/hooks';
import useAppContext from 'flow/hooks/useAppContext';
import { getOwnActiveCalls } from 'flow/selectors/call-selectors';
import { getRecentlyContactedContactsAndExtensions } from 'flow/selectors/extension-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { Contact } from 'flow/types/Contact';
import { Extension } from 'flow/types/Extension';

import { TypedMap } from 'flow/types/TypedMap';
import { isExtensionKey } from 'flow/utils';

const MAXIMUM_TO_SHOW = 6;

const RecentContactedCentralSwitchWidget = () => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const numOwnActiveCalls = useAppSelector(getOwnActiveCalls).size;
    const recentlyContactedContactsAndExtensions = useAppSelector(
        getRecentlyContactedContactsAndExtensions,
    );

    const previousActiveCalls = usePrevious(numOwnActiveCalls) ?? 0;

    useEffect(() => {
        dispatch(fetchCallRecordsForExtension(ownExtensionKey, ['contact']));
    }, [ownExtensionKey]);

    useEffect(() => {
        if (numOwnActiveCalls < previousActiveCalls) {
            fetchCallRecordsForExtension(ownExtensionKey, ['contact']);
        }
    }, [numOwnActiveCalls, previousActiveCalls, ownExtensionKey]);

    const renderExtension = (suggestion: TypedMap<Extension>) => {
        const suggestionKey = suggestion.get('key');

        if (ownExtensionKey === suggestionKey) return null;

        return (
            <ListItemContact
                key={suggestionKey}
                contact={suggestion.get('contact')}
                contactType={'extension'}
                extensionKey={suggestionKey}
            />
        );
    };

    const renderContact = (suggestion: TypedMap<Contact>) => {
        const suggestionKey = suggestion.get('key');
        return (
            <ListItemContact
                key={suggestionKey}
                contact={suggestion}
                contactType={'externalContacts'}
            />
        );
    };

    const getContent = () => {
        if (recentlyContactedContactsAndExtensions.isEmpty()) {
            return <CentralSwitchEmptyState uniqueId={'RecentContacted'} />;
        }

        return (
            <main>
                <ul className="px-4 pb-4">
                    {recentlyContactedContactsAndExtensions
                        .toList()
                        .toArray()
                        .slice(0, MAXIMUM_TO_SHOW)
                        .map((suggestion: TypedMap<Contact | Extension>) => {
                            const suggestionKey = suggestion.get('key');
                            const isExtension = isExtensionKey(suggestionKey);
                            if (isExtension) {
                                return renderExtension(
                                    suggestion as TypedMap<Extension>,
                                );
                            }
                            return renderContact(
                                suggestion as TypedMap<Contact>,
                            );
                        })}
                </ul>
            </main>
        );
    };

    return (
        <>
            <CentralSwitchHeader>
                {i18next.t('central_switch.recently_contacted.description')}
            </CentralSwitchHeader>
            {getContent()}
        </>
    );
};

export default RecentContactedCentralSwitchWidget;
